import { joiResolver } from '@hookform/resolvers/joi';
import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { AxiosError } from 'axios';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { TableActionTypes, Typography } from 'web-library';
import { useGetCandidate } from '../../../api-refactored/user/useGetCandidate';
import { useGetCoordinator } from '../../../api-refactored/user/useGetCoordinator';
import {
  RequestApproveRequest,
  usePostRequestApprove,
} from '../../../api-refactored/user/usePostRequestApprove';
import { usePostRequestCancel } from '../../../api-refactored/user/usePostRequestCancel';
import {
  RequestRejectRequest,
  usePostRequestReject,
} from '../../../api-refactored/user/usePostRequestReject';
import { UseCustomMutationReturn } from '../../../api-refactored/utils/useCustomMutation';
import { UseCustomQueryReturn } from '../../../api-refactored/utils/useCustomQuery';
import { GenericErrorResponse } from '../../../api';
import { ActionRequestRequest } from '../../../api/user/model';
import {
  GET_ACTION_REQUEST_LIST_QUERY_KEY,
  GET_ACTION_REQUESTER_REQUEST_LIST_QUERY_KEY,
} from '../../../constants/queryKeys';
import { ServiceError } from '../../../hooks/error';
import { ActionRequest, RejectRequestData } from '../../../models';
import schemaRejectRequest from '../schema/rejectRequest';
import { useUserIndexSplit } from './useAccountManagement';

export const taskListShareStyle = (theme: AugmentedTheme) => ({
  detailCard: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  reasonCard: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    backgroundColor: theme.color.background.bgAlt,
  },
  listItem: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  inline: {
    display: 'inline',
  },
  reasonTextParagraph: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    width: '100%',
  },
  reasonText: {
    marginBottom: theme.spacing(2),
  },
  requestActionButton: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(2),
    width: 100,
  },
  flexBox: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
  },
  flexText: {
    flexGrow: 1,
  },
  searchBar: {},
  searchInput: {
    width: '100%',
  },
  sortLabel: {
    marginRight: theme.spacing(1),
  },
});

const useStyles = makeStyles()((theme) =>
({
  reasonText: {
    marginBottom: theme.spacing(0),
  },
}));

export interface ReasonReturn {
  /**
   * Reasons paragraph to render
   */
  reason: React.ReactNode[];
}

/**
 * A hook to render reason string to Typography paragraph based on new lines
 */
export const useReason = (raw: string): ReasonReturn => {
  const { classes } = useStyles();
  const reason = useMemo(
    () =>
      raw.split('\n').map((str) => {
        if (str !== '') {
          return (
            <Typography key={str} className={classes.reasonText} variant="body2">
              {str}
            </Typography>
          );
        }
        return <br key={str} />;
      }),
    [raw, classes.reasonText]
  );

  return {
    reason,
  };
};

/**
 * A hook to provide search function for task list
 */
export const useTaskSearch = (data: ActionRequest[] | undefined, search: string): ActionRequest[] =>
  useMemo(() => {
    if (!data) {
      return [];
    }
    return data.filter(
      (item) =>
        (!!item && !!item.target && item.target.toLowerCase().includes(search.toLowerCase())) ||
        (!!item && !!item.identity && item.identity.toLowerCase().includes(search.toLowerCase()))
    );
  }, [data, search]);

export interface TaskSortReturn {
  /**
   * Result data of the task list search
   */
  result: ActionRequest[];
  /**
   * Ascending for the search
   */
  ascSort: boolean;
  /**
   * Function to update the sort criteria, will update the result if changed
   */
  setAscSort: Dispatch<SetStateAction<boolean>>;
}

/**
 * A hook to provide sort function for task list
 */
export const useTaskSort = (
  data: ActionRequest[] | undefined,
  tabIndex: number // to decide which field to check
): TaskSortReturn => {
  const [ascSort, setAscSort] = useState(false); // default latest
  const result = useMemo(() => {
    if (!data) {
      return [];
    }
    return data.sort((a, b) => {
      if (tabIndex === 0) {
        if (ascSort) {
          return a.createdAt - b.createdAt;
        }
        return b.createdAt - a.createdAt;
      }
      if (ascSort) {
        return a.resolvedAt - b.resolvedAt;
      }
      return b.resolvedAt - a.resolvedAt;
    });
  }, [data, tabIndex, ascSort]);

  return {
    ascSort,
    setAscSort,
    result,
  };
};

export interface TaskListReturn {
  /**
   * React hook form instace for reject form
   */
  form: UseFormReturn<Record<string, any>>;
  /**
   * Error message for approve/reject api function call
   */
  mutationError: null | string;
  /**
   * React-query object for retrieving request list data
   */
  requestQuery: UseCustomQueryReturn<ActionRequest[], ServiceError>;
  /**
   * Current selected request
   */
  selectedRequest: null | ActionRequest;
  /**
   * Function to update current selected request
   */
  setSelectedRequest: Dispatch<SetStateAction<null | ActionRequest>>;
  /**
   * Function to approve current selected request
   */
  submitApprove: () => void;
  /**
   * Function to reject current selected request
   */
  submitReject: (rejectData: RejectRequestData) => void;

  /**
   * Function to cancel pending request.
   * @param cancelData the reason data for cancellation.
   */
  submitCancel: () => void;
  /**
   * React query approve mutation object
   */
  approveRequestMutation: UseCustomMutationReturn<void, AxiosError, RequestApproveRequest, unknown>;
  /**
   * React query reject mutation object
   */
  rejectRequestMutation: UseCustomMutationReturn<
    void,
    AxiosError<GenericErrorResponse>,
    RequestRejectRequest,
    unknown
  >;
}

/**
 * A hook to retrieve task list data with different task type, and functions to approve task & reject task
 * @param selectedTaskType Task type to retrieve for the request list, will refetch the list if task type change
 * @returns
 */
export const useTaskList = (
  selectedTaskType: string,
  pending: boolean,
  requestReactQuery: (input: {
    params: ActionRequestRequest;
    onSuccess: (res: any) => void;
    onError?: (error: AxiosError<GenericErrorResponse>) => void;
  }) => UseCustomQueryReturn<ActionRequest[], ServiceError>,
  queryType: 'approver' | 'requester',
  tableDispatch: React.Dispatch<TableActionTypes>,
  shouldLoad: boolean = true,
  setShouldLoad?: (newState: boolean) => void
): TaskListReturn => {
  const queryClient = useQueryClient();

  const [mutationError, setMutationError] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<ActionRequest | null>(null);

  // get api
  const requestQuery = requestReactQuery({
    params: { type: selectedTaskType, pending },
    onSuccess: (res) => {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: res,
          count: res.length,
        },
      } as TableActionTypes);
    },
  });

  const mutationSuccess = useCallback(() => {
    queryClient
      .invalidateQueries(
        queryType === 'approver'
          ? GET_ACTION_REQUEST_LIST_QUERY_KEY
          : GET_ACTION_REQUESTER_REQUEST_LIST_QUERY_KEY
      )
      .then(() => null);
    setSelectedRequest(null);
  }, [queryClient, queryType]);

  const mutationFailure = useCallback(() => {
    setMutationError('Something wrong.');
  }, []);

  const requestRefetch = requestQuery.refetch;

  useEffect(() => {
    requestRefetch().then(() => null);
  }, [requestRefetch, selectedTaskType, pending]);

  useEffect(() => {
    setMutationError(null);
  }, [selectedRequest]);

  // post approve api
  const approveRequestMutation = usePostRequestApprove({
    onSuccess: mutationSuccess,
    onError: mutationFailure,
  });

  // post reject api
  const rejectRequestMutation = usePostRequestReject({
    onSuccess: mutationSuccess,
    onError: mutationFailure,
  });

  // post cancel api
  const cancelRequestMutation = usePostRequestCancel({
    onSuccess: mutationSuccess,
    onError: mutationFailure,
  });

  const approveReset = approveRequestMutation.reset;
  const rejectReset = rejectRequestMutation.reset;
  const cancelReset = cancelRequestMutation.reset;

  useEffect(() => {
    if (selectedRequest) {
      setShouldLoad?.(true);
    }
    if (!selectedRequest && shouldLoad) {
      setShouldLoad?.(false);
      approveReset();
      rejectReset();
      cancelReset();
    }
  }, [selectedRequest, approveReset, rejectReset, cancelReset, shouldLoad, setShouldLoad]);

  const form = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: joiResolver(schemaRejectRequest),
  });

  const submitApprove = () => {
    approveRequestMutation.mutate({ id: selectedRequest ? selectedRequest.requestId : 0 });
    approveReset();
  };

  const submitCancel = () => {
    cancelRequestMutation.mutate({ id: selectedRequest ? selectedRequest.requestId : 0 });
    cancelReset();
  };

  const submitReject = (rejectData: RejectRequestData) => {
    const reasonText = rejectData.reason.split('↵');
    const processed = reasonText.reduce((final, current) => `${final}\\n${current}`, '');
    rejectRequestMutation.mutate({
      id: selectedRequest ? selectedRequest.requestId : 0,
      reason: processed,
    });
    rejectReset();
  };

  // get all ids in the list
  const requestIds = useMemo(
    () => (requestQuery.data ? requestQuery.data?.map((item) => item.requestId) : []),
    [requestQuery.data]
  );
  // check selected still remain in list, if not clear it
  useEffect(() => {
    if (selectedRequest?.requestId && !requestIds.includes(selectedRequest?.requestId)) {
      setSelectedRequest(null);
    }
  }, [requestIds, selectedRequest?.requestId]);

  return {
    selectedRequest,
    setSelectedRequest,
    requestQuery,
    form,
    submitApprove,
    submitReject,
    submitCancel,
    mutationError,
    approveRequestMutation,
    rejectRequestMutation,
  };
};

/**
 * A hook to call candidate or coordinator query to get details based on selected request
 * @param selectedRequest
 * @returns
 */
export const useIndieQuery = (selectedRequest: ActionRequest | null) => {
  const { candidateUserIndex, centreUserIndex } = useUserIndexSplit(
    selectedRequest?.targetId,
    selectedRequest?.accountType
  );
  const { data: candidateData } = useGetCandidate(candidateUserIndex);
  const { data: coordinatorData } = useGetCoordinator(centreUserIndex);

  return {
    candidateData,
    coordinatorData,
  };
};
